// Deterministic noise toolkit — the primitives every forged surface is built
// out of. Moved here verbatim from apps/web/src/render/forge.ts when the forge
// became an offline build step: the same functions now serve BOTH the offline
// baker (tools/assets) and the runtime forges the client still runs
// (render/attire.ts, render/textures.ts, render/signage.ts, render/city.ts),
// so there is exactly one implementation and the sheets they produce cannot
// drift apart.
//
// Nothing in this file may import three.js, the DOM, or node. It is pure
// numerics over Float32Array, which is what lets it run in a browser tab and
// in a build script without a shim.

/** Mulberry32 — tiny deterministic PRNG. */
export function mulberry(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const fade = (t: number): number => t * t * (3 - 2 * t);

export const clamp01 = (t: number): number => Math.min(1, Math.max(0, t));

export const mix = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Tileable value noise on a `px` × `py` lattice, sampled bilinearly with a
 * smoothstep fade. Independent periods matter: wood grain and brushed metal
 * are noise that is long in one axis and short in the other, and faking that
 * by stretching UVs stretches the FILTERING too (the classic smeared-grain
 * look).
 */
export function noise2(seed: number, px: number, py: number): (u: number, v: number) => number {
  const rng = mulberry(seed);
  const lattice = new Float32Array(px * py);
  for (let i = 0; i < lattice.length; i++) lattice[i] = rng();
  return (u, v) => {
    const x = u * px;
    const y = v * py;
    const xf = Math.floor(x);
    const yf = Math.floor(y);
    const x0 = ((xf % px) + px) % px;
    const y0 = ((yf % py) + py) % py;
    const x1 = (x0 + 1) % px;
    const y1 = (y0 + 1) % py;
    const fx = fade(x - xf);
    const fy = fade(y - yf);
    const a = lattice[y0 * px + x0] ?? 0;
    const b = lattice[y0 * px + x1] ?? 0;
    const c = lattice[y1 * px + x0] ?? 0;
    const d = lattice[y1 * px + x1] ?? 0;
    return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
  };
}

/** Square tileable value-noise octave (the render/textures.ts signature). */
export function noiseOctave(seed: number, period: number): (u: number, v: number) => number {
  return noise2(seed, period, period);
}

/** Tileable fbm in [0, 1] from stacked octaves. */
export function makeFbm(
  seed: number,
  periods: readonly number[],
): (u: number, v: number) => number {
  const octaves = periods.map((p, i) => noiseOctave(seed + i * 131, p));
  let norm = 0;
  for (let i = 0; i < octaves.length; i++) norm += 0.5 ** i;
  return (u, v) => {
    let sum = 0;
    for (let i = 0; i < octaves.length; i++) sum += (octaves[i]?.(u, v) ?? 0) * 0.5 ** i;
    return sum / norm;
  };
}

/**
 * Anisotropic fbm: each octave's v-period is `aspect` times its u-period.
 * `aspect` < 1 stretches the noise ALONG u — plank grain, brushed brass,
 * canvas warp, wind ripple. This is the primitive that separates "a material
 * with a direction" from "another blob of noise".
 */
export function makeFbmAniso(
  seed: number,
  periods: readonly number[],
  aspect: number,
): (u: number, v: number) => number {
  const octaves = periods.map((p, i) =>
    noise2(seed + i * 131, Math.max(1, Math.round(p)), Math.max(1, Math.round(p * aspect))),
  );
  let norm = 0;
  for (let i = 0; i < octaves.length; i++) norm += 0.5 ** i;
  return (u, v) => {
    let sum = 0;
    for (let i = 0; i < octaves.length; i++) sum += (octaves[i]?.(u, v) ?? 0) * 0.5 ** i;
    return sum / norm;
  };
}

/** Tileable ridged fbm in [0, 1]: sharp creases where value noise has zeros
 *  — veins, fracture lines, trowel edges, growth rings. */
export function makeRidged(
  seed: number,
  periods: readonly number[],
): (u: number, v: number) => number {
  const octaves = periods.map((p, i) => noiseOctave(seed + i * 977, p));
  let norm = 0;
  for (let i = 0; i < octaves.length; i++) norm += 0.5 ** i;
  return (u, v) => {
    let sum = 0;
    for (let i = 0; i < octaves.length; i++) {
      const n = octaves[i]?.(u, v) ?? 0.5;
      sum += (1 - Math.abs(n * 2 - 1)) * 0.5 ** i;
    }
    return sum / norm;
  };
}

/**
 * Tileable cellular (Worley) field, returned as F1 and F2 in UV units.
 * `cx`/`cy` are cell counts across the sheet; independent so a field can be
 * stretched (paving courses, tile rows) without stretching its filtering.
 */
export function cellular(
  seed: number,
  cx: number,
  cy: number,
): (u: number, v: number, out: [number, number]) => void {
  const rng = mulberry(seed);
  const px = new Float32Array(cx * cy);
  const py = new Float32Array(cx * cy);
  for (let j = 0; j < cy; j++) {
    for (let i = 0; i < cx; i++) {
      px[j * cx + i] = (i + rng()) / cx;
      py[j * cx + i] = (j + rng()) / cy;
    }
  }
  const wrap = (d: number): number => (d > 0.5 ? d - 1 : d < -0.5 ? d + 1 : d);
  return (u, v, out) => {
    const ci = Math.floor(u * cx);
    const cj = Math.floor(v * cy);
    let f1 = 4;
    let f2 = 4;
    for (let dj = -1; dj <= 1; dj++) {
      const j = (((cj + dj) % cy) + cy) % cy;
      for (let di = -1; di <= 1; di++) {
        const i = (((ci + di) % cx) + cx) % cx;
        const dx = wrap(u - (px[j * cx + i] ?? 0));
        const dy = wrap(v - (py[j * cx + i] ?? 0));
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < f1) {
          f2 = f1;
          f1 = d;
        } else if (d < f2) {
          f2 = d;
        }
      }
    }
    out[0] = f1;
    out[1] = f2;
  };
}

/** Stable per-index hash in [0, 1) — per-plank, per-slab, per-tile jitter. */
export function hash1(seed: number, i: number): number {
  let t = (Math.imul(i ^ seed, 0x27d4eb2d) ^ (seed << 5)) >>> 0;
  t = Math.imul(t ^ (t >>> 15), 0x85ebca6b) >>> 0;
  return ((t ^ (t >>> 13)) >>> 0) / 4294967296;
}
